import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { ClipboardCheck } from '@untitledui/icons/ClipboardCheck';
import { Flag05 } from '@untitledui/icons/Flag05';
import { Gift01 } from '@untitledui/icons/Gift01';
import { LogOut01 } from '@untitledui/icons/LogOut01';
import { Luggage02 } from '@untitledui/icons/Luggage02';
import { MessageCircle02 } from '@untitledui/icons/MessageCircle02';
import { Rows01 } from '@untitledui/icons/Rows01';
import { Settings02 } from '@untitledui/icons/Settings02';
import { Settings04 } from '@untitledui/icons/Settings04';
import { User01 } from '@untitledui/icons/User01';

import type { NavItem as NavItemType } from '@/components/navigation/header-navigation';

import { HeaderNavigationBase } from '@/components/navigation/header-navigation';
import { BASE_PATH } from '@/constants/config';

import { useIsPublicApplyRoute } from './-hooks/use-is-public-apply-route';

export const Route = createFileRoute('/participants/_public')({
  component: PublicLayout,
});

function PublicLayout() {
  const { user } = Route.useRouteContext();
  const location = useLocation();
  const currentPath = location.pathname;
  const isPublicApply = useIsPublicApplyRoute();

  // The public apply page is a single-purpose landing page reached from an
  // ad or an email, and it may be embedded in an iframe on an external page:
  // no header, so the screener question is the only thing on it and there is
  // nothing to click away to. No background or min-height either — the page
  // must blend into whatever hosts it, and the document must be no taller
  // than its content so the broadcast height (see
  // -public-apply/use-broadcast-content-height.ts) tracks the form, not the
  // frame it happens to be rendered in.
  if (isPublicApply) {
    return (
      <main>
        <Outlet />
      </main>
    );
  }

  // Show full nav for authenticated users so they don't lose navigation context.
  // Unauthenticated users get no nav items (logo only).
  const navItems: NavItemType[] = user
    ? [
        {
          label: 'Projects',
          href: `${BASE_PATH}/participants/projects`,
          current: currentPath.startsWith('/participants/projects'),
          icon: Rows01,
        },
        {
          label: 'My Projects',
          href: `${BASE_PATH}/participants/my-projects`,
          current: currentPath.startsWith('/participants/my-projects'),
          icon: ClipboardCheck,
        },
        {
          label: 'Messages',
          href: `${BASE_PATH}/participants/messages`,
          current: currentPath.startsWith('/participants/messages'),
          icon: MessageCircle02,
        },
        {
          label: 'Settings',
          href: `${BASE_PATH}/participants/settings`,
          current: currentPath.startsWith('/participants/settings'),
          icon: Settings02,
          items: [
            {
              label: 'Profile info',
              href: `${BASE_PATH}/participants/settings?tab=profile-info`,
              icon: User01,
            },
            {
              label: 'Professional background',
              href: `${BASE_PATH}/participants/settings?tab=professional-background`,
              icon: Luggage02,
            },
            {
              label: 'Social accounts',
              href: `${BASE_PATH}/participants/settings?tab=social-accounts`,
              icon: Flag05,
            },
            {
              label: 'Account settings',
              href: `${BASE_PATH}/participants/settings?tab=account-settings`,
              icon: Settings04,
            },
            {
              label: 'Referrals',
              href: `${BASE_PATH}/participants/settings?tab=referrals`,
              icon: Gift01,
            },
            {
              divider: true,
            },
            {
              label: 'Sign out',
              href: `${BASE_PATH}/sign-out`,
              icon: LogOut01,
            },
          ],
        },
      ]
    : [];

  return (
    <div className="flex min-h-screen flex-col bg-primary">
      <HeaderNavigationBase
        activeUrl={currentPath}
        items={navItems}
        showAvatarDropdown={!!user}
        user={user}
        logoHref={user ? `${BASE_PATH}/participants/projects` : undefined}
      />
      <main className="flex-1 bg-secondary">
        <Outlet />
      </main>
    </div>
  );
}
